import { useState, useEffect, useMemo, useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useMediaQuery } from '@mui/material';

import { useRoomContext } from '@src/contexts';
import { useEmojiAnimations } from '@src/hooks';
import { TEmojiId } from '@src/types';

interface TReactionEntry {
  emoji: TEmojiId;
  user: string;
  id: string;
  side: 'left' | 'right';
  xPosition: number;
  sidePosition: string;
}

const ANIMATION_DURATION_MS = 5000;

const useAnimatedReactions = (isPip: boolean = false) => {
  const { emoji, currentPeer } = useRoomContext();
  const isMobile = useMediaQuery('(max-width:768px)');
  const { animations } = useEmojiAnimations();

  // state now holds the pre-computed positions
  const [reactions, setReactions] = useState<TReactionEntry[]>([]);

  const removeReaction = useCallback((id: string) => {
    setReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Whenever a new emoji arrives, compute its position once and schedule its removal
  useEffect(() => {
    if (!emoji) return;

    setReactions((prev) => {
      const idx = prev.length;
      const side: 'left' | 'right' = idx % 2 === 0 ? 'left' : 'right';

      // PiP specific logic
      let xPositions: number[];
      let stylePositions: number[];

      if (isPip) {
        // In PiP, start closer to center (10, 20, 30%)
        stylePositions = [10, 20, 30];

        // Move INWARDS. If side is left, move right (+). If side is right, move left (-).
        const direction = side === 'left' ? 1 : -1;
        // Small floating movement
        xPositions = [20, 40, 60].map((x) => x * direction);
      } else {
        // Mobile / Desktop logic
        stylePositions = [-10, 0, 20];
        xPositions = isMobile ? [80, 100] : [-30, 30, 50, 70];
      }

      const xPosition = xPositions[idx % xPositions.length];
      const sidePosition = `${stylePositions[idx % stylePositions.length]}%`;
      const id = `${emoji.peer_name}-${Date.now()}-${nanoid()}`;

      // schedule removal for this specific id
      setTimeout(() => removeReaction(id), ANIMATION_DURATION_MS);

      if (document.hidden && !isPip) {
        return prev;
      }

      return [
        ...prev,
        {
          emoji: emoji.emoji!,
          user: emoji.peer_name,
          id,
          side,
          xPosition,
          sidePosition,
        },
      ];
    });
  }, [emoji, isMobile, removeReaction, isPip]);

  useEffect(() => {
    setReactions([]);
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch((error) => {
        console.error('Failed to exit Picture-in-Picture mode:', error);
      });
    }
  }, [isMobile]);

  // Now just attach animationData to each pre-positioned reaction
  const reactionAnimations = useMemo(
    () =>
      reactions.map((r) => {
        const animationData = animations[r.emoji];
        if (!animationData) return null;
        return { ...r, animationData };
      }),
    [animations, reactions]
  );

  // clear on tab-visibility (your existing logic)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setReactions([]);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return {
    animations,
    currentPeer,
    isMobile,
    reactionAnimations,
    removeReaction,
  };
};

export default useAnimatedReactions;
